"use client"
import { PaymentsModal } from "@/components/payments-modal"
import { SharedImageModal } from "@/components/shared-image-modal"
import { Footer } from "@/components/footer"
import Image from "next/image"
import { useState } from "react"
import { ChevronDown } from "lucide-react"

const BIO_MEDIA_ROOT = "bio"
const BIO_MEDIA_HOST = (process.env.NEXT_PUBLIC_R2_PUBLIC_DOMAIN || "media.akilesecosystem.com")
  .replace(/^https?:\/\//, "")
  .replace(/\/$/, "")

const BIO_IMAGE_MAP: Record<string, string> = {
  "bg-gathering-wood-bio.png": "bg-gathering-wood-bio.png",
  "stage1-screenprinting-bjork.png": "stage1-screenprinting-no-body-can-do-it-like-you-bio.png",
  "stage1-mural-olaConCon.png": "stage1-mural-ola-concon-bio.png",
  "stage1-aerography-smoking-monkey.png": "stage1-aerography-smoking-monkey-bio.png",
  "stage1-electronics -waste-assemblage-techno-dragon.png": "stage1-electronics -waste-assemblage-techno-dragon-bio.png",
  "stage1-recycled-wood-assemblage-composition1.png": "stage1-recycled-wood-assemblage-composition-1-bio.png",
  "stage1-mural-paco-malo.png": "stage1-mural-paco-malo-bio.png",
  "stage2-recycled-wood-assemblage-recuerdos-del-mar.png": "stage2-recycled-wood-assemblage-recuerdos-del-mar-bio.png",
  "stage2-recycled-wood-picture-in-workshop.png": "stage2-recycled-wood-picture-in-workshop-bio.png",
  "stage2-recycled-wood-assemblage-resistencia.png": "stage2-recycled-wood-assemblage-resistencia-bio.png",
  "stage2-recycled-wood-punta-de-penero.png": "stage2-recycled-wood-punta-de-penero-bio.png",
  "stage2-recycled-wood-workshop.png": "stage2-recycled-wood-workshop-bio.png",
  "stage2-recycled-wood-assemblage-sculpture-humanae-creaturae.png": "stage2-recycled-wood-assemblage-sculpture-humanae-creaturae-bio.png",
  "stage2-recycled-wood-assemblage-sculpture-canodromo.png": "stage2-recycled-wood-assemblage-sculpture-canodromo-bio.png",
  "stage2-recycled-wood-mirror-frames-hotel-boutique2.png": "stage2-recycled-wood-mirror-frames-hotel-boutique-2-bio.png",
  "stage2-acrylic-painting-bituaya1.png": "stage2-acrylic-painting-bituaya-1-bio.png",
  "stage2-recycled-wood-mirror-frames-hotel-boutique.png": "stage2-recycled-wood-mirror-frames-hotel-boutique-bio.png",
  "stage3-exhibition-picture-with-lios-junity.png": "stage3-exhibition-picture-with-lios-junity-bio.png",
  "stage3-recycled-wood-assemblage-table-sofa-furniture-art.png": "stage3-recycled-wood-assemblage-table-sofa-furniture-art-bio.png",
  "stage3-recycled-wood-2.5D-assemblage-artwork.png": "stage3-interdimensional-assemblage-artwork-bio.png",
  "stage3-recycled-wood-assemblage-mirror-frames-store.png": "stage3-mirror-frames-store-assemblage--bio.png",
  "stage3-exhibition-priced-sculpture.png": "stage3-exhibition-priced-sculpture-bio.png",
  "stage3-recycled-wood-assemblage-mirror-frames-hotel.png": "stage3-recycled-wood-assemblage-mirror-frames-hotel-bio.png",
  "stage3-exhibition-priced-artwork.png": "stage3-exhibition-priced-artwork-bio.png",
  "stage3-recycled-wood-assemblage-infinito.png": "stage3-recycled-wood-assemblage-infinito-bio.png",
  "stage3-recycled-wood-assemblage-sunset1.png": "stage3-sunset-1-assemblage-bio.png",
  "stage3-exhibition-national-museum-alejandro-otero.png": "stage3-exhibition-national-museum-alejandro-otero-bio.png",
  "stage3-recycled-wood-assemblage-massive-artwork.png": "stage3-recycled-wood-assemblage-massive-artwork-bio.png",
  "stage4-online-courses.jpg": "stage4-game-design-online-courses-bio.png",
  "stage4-Seven-Elements-game-design.jpg": "stage4-seven-elements-game-design-bio.png",
  "stage4--Exiliado-P5-Animation.png": "stage4-exiliado-p5-animation-bio.png",
  "stage4-certificate-digital-illustration-animation.png": "stage4-certificate-digital-illustration-animation-bio.png",
  "stage4-digital-art-scape.png": "stage4-digital-art-scape-bio.png",
  "stage4-future-taxi-3D-animation.png": "stage4-future-taxi-three-d-model-animation-bio.png",
  "stage4-remares-project.png": "stage4-remares-project-bio.png",
  "stage4-polygonal-hd-acrylic-painting.png": "stage4-polygonal-hd-acrylic-on-canvas-bio.png",
  "stage5-Abstract-Sunset-acrylic-painting.jpg": "stage5-abstract-sunset-acrylic-on-canvas-bio.jpg",
  "stage5-TheGarden-acrylic-painting.jpg": "stage5-the-garden-acrylic-on-canvas-bio.jpg",
  "stage5-Mush-Cascade-AcrylicArtwork.jpg": "stage5-mush-cascade-acrylic-on-canvas-bio.jpg",
  "stage5-WhaleA-digital-art.jpg": "stage5-humpback-whale-digital-art-bio.jpg",
  "stage5-Elephant-King-digitization.jpg": "stage5-elephant-king-digitization-bio.jpg",
  "stage5-BabyBuddha-Digitization.jpg": "stage5-baby-buddha-2-digitization-bio.png",
  "stage5-Bitcoin1-digitization.jpg": "stage5-bitcoin-1-digitization-bio.jpg",
}

const bioImage = (filename: string) => {
  const mapped = BIO_IMAGE_MAP[filename] ?? filename
  const normalized = mapped.replace(/^\/+/, "")
  return `https://${BIO_MEDIA_HOST}/${BIO_MEDIA_ROOT}/${normalized}`
}

type BioGalleryItem = {
  filename: string
  alt: string
  caption: string
}

const BIO_STAGE_IMAGES: Record<1 | 2 | 3 | 4 | 5, BioGalleryItem[]> = {
  1: [
    { filename: "stage1-screenprinting-bjork.png", alt: "Screen printing work featuring Björk", caption: "Screen Printing - Björk" },
    { filename: "stage1-mural-olaConCon.png", alt: "Mural painting - Ola Con Con", caption: "Mural - Ola Con Con" },
    { filename: "stage1-aerography-smoking-monkey.png", alt: "Aerography artwork - Smoking Monkey", caption: "Aerography - Smoking Monkey" },
    { filename: "stage1-electronics -waste-assemblage-techno-dragon.png", alt: "Electronics waste assemblage - Techno Dragon", caption: "Electronics Assemblage - Techno Dragon" },
    { filename: "stage1-recycled-wood-assemblage-composition1.png", alt: "Recycled wood assemblage composition", caption: "Recycled Wood Assemblage - Composition 1" },
    { filename: "stage1-mural-paco-malo.png", alt: "Mural painting - Paco Malo", caption: "Mural - Paco Malo" },
  ],
  2: [
    { filename: "stage2-recycled-wood-assemblage-recuerdos-del-mar.png", alt: "Recycled wood assemblage - Recuerdos del Mar", caption: "Recycled Wood Assemblage - Recuerdos del Mar" },
    { filename: "stage2-recycled-wood-picture-in-workshop.png", alt: "Recycled wood artwork in workshop", caption: "Workshop Picture" },
    { filename: "stage2-recycled-wood-assemblage-resistencia.png", alt: "Recycled wood assemblage - Resistencia", caption: "Recycled Wood Assemblage - Resistencia" },
    { filename: "stage2-recycled-wood-punta-de-penero.png", alt: "Recycled wood assemblage - Punta de Penero", caption: "Recycled Wood Assemblage - Punta de Penero" },
    { filename: "stage2-recycled-wood-workshop.png", alt: "Recycled wood workshop", caption: "Workshop" },
    { filename: "stage2-recycled-wood-assemblage-sculpture-humanae-creaturae.png", alt: "Recycled wood assemblage sculpture - Humanae Creaturae", caption: "Sculpture - Humanae Creaturae" },
    { filename: "stage2-recycled-wood-assemblage-sculpture-canodromo.png", alt: "Recycled wood assemblage sculpture - Canodromo", caption: "Sculpture - Canodromo" },
    { filename: "stage2-recycled-wood-mirror-frames-hotel-boutique2.png", alt: "Recycled wood mirror frames for hotel boutique", caption: "Mirror Frames - Hotel Boutique 2" },
    { filename: "stage2-acrylic-painting-bituaya1.png", alt: "Acrylic painting - Bituaya", caption: "Acrylic Painting - Bituaya" },
    { filename: "stage2-recycled-wood-mirror-frames-hotel-boutique.png", alt: "Recycled wood mirror frames for hotel boutique", caption: "Mirror Frames - Hotel Boutique" },
  ],
  3: [
    { filename: "stage3-exhibition-picture-with-lios-junity.png", alt: "Exhibition picture with Lios and Junity", caption: "Exhibition with Lios & Junity" },
    { filename: "stage3-recycled-wood-assemblage-table-sofa-furniture-art.png", alt: "Recycled wood assemblage furniture art - table and sofa", caption: "Furniture Art - Table & Sofa" },
    { filename: "stage3-recycled-wood-2.5D-assemblage-artwork.png", alt: "Recycled wood 2.5D assemblage artwork", caption: "2.5D Assemblage Artwork" },
    { filename: "stage3-recycled-wood-assemblage-mirror-frames-store.png", alt: "Recycled wood assemblage mirror frames in store", caption: "Mirror Frames - Store" },
    { filename: "stage3-exhibition-priced-sculpture.png", alt: "Exhibition with priced sculpture", caption: "Exhibition - Priced Sculpture" },
    { filename: "stage3-recycled-wood-assemblage-mirror-frames-hotel.png", alt: "Recycled wood assemblage mirror frames for hotel", caption: "Mirror Frames - Hotel" },
    { filename: "stage3-exhibition-priced-artwork.png", alt: "Exhibition with priced artwork", caption: "Exhibition - Priced Artwork" },
    { filename: "stage3-recycled-wood-assemblage-infinito.png", alt: "Recycled wood assemblage - Infinito", caption: "Recycled Wood Assemblage - Infinito" },
    { filename: "stage3-recycled-wood-assemblage-sunset1.png", alt: "Recycled wood assemblage - Sunset", caption: "Recycled Wood Assemblage - Sunset" },
    { filename: "stage3-exhibition-national-museum-alejandro-otero.png", alt: "Exhibition at National Museum Alejandro Otero", caption: "National Museum Alejandro Otero" },
    { filename: "stage3-recycled-wood-assemblage-massive-artwork.png", alt: "Recycled wood assemblage massive artwork", caption: "Big Assemblage Artwork" },
  ],
  4: [
    { filename: "stage4-online-courses.jpg", alt: "Online courses and digital learning", caption: "Online Courses" },
    { filename: "stage4-Seven-Elements-game-design.jpg", alt: "Seven Elements game design project", caption: "Seven Elements - Game Design" },
    { filename: "stage4--Exiliado-P5-Animation.png", alt: "Exiliado - P5 Animation", caption: "Exiliado - P5 Animation" },
    { filename: "stage4-certificate-digital-illustration-animation.png", alt: "Certificate in Digital Illustration and Animation", caption: "Digital Illustration & Animation Certificate" },
    { filename: "stage4-digital-art-scape.png", alt: "Digital art - Scape", caption: "Digital Art - Scape" },
    { filename: "stage4-future-taxi-3D-animation.png", alt: "Future Taxi 3D animation project", caption: "Future Taxi - 3D Animation" },
    { filename: "stage4-remares-project.png", alt: "Remares project - house building from recycled plastic", caption: "Remares Project" },
    { filename: "stage4-polygonal-hd-acrylic-painting.png", alt: "Polygonal HD acrylic painting", caption: "Polygonal HD - Acrylic Painting" },
  ],
  5: [
    { filename: "stage5-Abstract-Sunset-acrylic-painting.jpg", alt: "Abstract Sunset - Acrylic Painting", caption: "Abstract Sunset - Acrylic" },
    { filename: "stage5-TheGarden-acrylic-painting.jpg", alt: "The Garden - Acrylic Painting", caption: "The Garden - Acrylic" },
    { filename: "stage5-Mush-Cascade-AcrylicArtwork.jpg", alt: "Mush Cascade - Acrylic Artwork", caption: "Mush Cascade - Acrylic" },
    { filename: "stage5-WhaleA-digital-art.jpg", alt: "WhaleA digital art", caption: "WhaleA - Digital Art" },
    { filename: "stage5-Elephant-King-digitization.jpg", alt: "Elephant King - Digitization", caption: "Elephant King - Digitization" },
    { filename: "stage5-BabyBuddha-Digitization.jpg", alt: "Baby Buddha - Digitization", caption: "Baby Buddha - Digitization" },
    { filename: "stage5-Bitcoin1-digitization.jpg", alt: "Bitcoin 1 - Digitization", caption: "Bitcoin 1 - Digitization" },
  ],
}

type BioImageGridProps = {
  items: BioGalleryItem[]
  onSelect: (src: string, alt: string) => void
}

function BioImageGrid({ items, onSelect }: BioImageGridProps) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((item) => {
          const src = bioImage(item.filename)
          return (
            <div key={item.filename} className="space-y-2">
              <Image
                src={src}
                alt={item.alt}
                width={400}
                height={300}
                className="w-full h-48 object-cover rounded-lg border border-border cursor-zoom-in"
                onClick={() => onSelect(src, item.alt)}
              />
              <p className="text-sm text-muted-foreground">{item.caption}</p>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default function BioPage() {
  const [lang, setLang] = useState<'en'|'es'>('en')
  const [expandedStages, setExpandedStages] = useState<Set<number>>(new Set())
  const [isPaymentsOpen, setIsPaymentsOpen] = useState(false)
  const [selectedImage, setSelectedImage] = useState<{src: string; alt: string} | null>(null)

  const toggleStage = (stageNumber: number) => {
    setExpandedStages(prev => {
      const newSet = new Set(prev)
      if (newSet.has(stageNumber)) {
        newSet.delete(stageNumber)
      } else {
        newSet.add(stageNumber)
      }
      return newSet
    })
  }

  const openGalleryImage = (src: string, alt: string) => {
    setSelectedImage({ src, alt })
  }

  return (
    <div className="min-h-screen bg-background text-foreground dark:bg-black dark:text-white pt-16 flex flex-col transition-colors duration-300">
      
      {/* Hero Banner Section */}
      <section className="relative w-full h-[60vh] min-h-[400px] overflow-hidden">
        <Image
          src={bioImage("bg-gathering-wood-bio.png")}
          alt="Artist gathering driftwood on the coast"
          fill
          className="object-cover"
          priority
          sizes="100vw"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/30 to-black/70 flex flex-col items-center justify-center">
          <div className="text-center px-4 max-w-4xl">
            <h1 className="text-5xl md:text-7xl font-black text-white tracking-tighter drop-shadow-2xl animate-in fade-in slide-in-from-bottom-8 duration-1000">
              AKILES
            </h1>
          </div>
        </div>
      </section>

      {/* Payments Modal */}
      <PaymentsModal
        isOpen={isPaymentsOpen}
        onClose={() => setIsPaymentsOpen(false)}
      />

      {/* Shared Image Modal */}
      <SharedImageModal
        isOpen={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        imageSrc={selectedImage?.src}
        imageAlt={selectedImage?.alt}
      />

      <main className="container mx-auto px-6 py-8 space-y-12">
        {/* Language Toggle */}
        <div className="flex justify-end">
          <div className="flex items-center gap-2 text-sm text-foreground">
            <button 
              onClick={() => setLang('en')} 
              className={`hover:underline ${lang === 'en' ? 'font-semibold' : ''}`}
            >
              English
            </button>
            <span className="text-muted-foreground">/</span>
            <button 
              onClick={() => setLang('es')} 
              className={`hover:underline ${lang === 'es' ? 'font-semibold' : ''}`}
            >
              Español
            </button>
          </div>
        </div>

        {/* Bio Content */}
        <section className="max-w-6xl mx-auto space-y-16">
          {/* Introduction */}
          <div className="prose dark:prose-invert max-w-none text-foreground">
            {lang === 'en' ? (
              <div className="space-y-6 text-lg leading-relaxed">
                <h2 className="text-3xl font-bold mb-6">Bio</h2>
                <p>
                  I&apos;m Aquiles, a Venezuelan-born (Caracas) artist and designer raised on Margarita Island and now based in the Pacific coast of Costa Rica. I graduated in Graphic Design from the R.L.A. University Institute and have lived, studied, and worked across Venezuela, USA, Chile, Uruguay, and now Costa Rica.
                </p>
                <p>
                  My practice bridges analog and digital: murals, aerography, screen printing, recycled-material assemblages, acrylic painting, and digital art for prints, stickers, apparel, and more. I&apos;ve exhibited in galleries and museums, collaborated with other artists, and have been teaching art lately. I&apos;ve also completed studies in game design and animation. I&apos;m a father of two—Sebastián and Padme—and an eternal learner who sees art as both craft and adventure.
                </p>
                <p>
                  My work has moved in stages. Some roared with growth—studios, expositions, museums. Others paused under the weight of exile and survival. Each phase left a mark on the way I imagine, create, build, and tell stories.
                </p>
              </div>
            ) : (
              <div className="space-y-6 text-lg leading-relaxed text-center italic text-muted-foreground">
                <p>... en proceso ...</p>
              </div>
            )}
          </div>

          {/* Stages */}
          {lang === 'en' ? (
            <div className="space-y-8">
              {/* Stage 1 */}
              <div className="bg-card/50 border-2 border-border/40 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
                <div 
                  onClick={() => toggleStage(1)}
                  className="p-8 cursor-pointer flex items-center justify-between group select-none"
                >
                  <div className="flex-grow">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-[10px] font-mono font-black px-2 py-0.5 bg-red-600/20 text-red-600 border border-red-600/30 rounded uppercase tracking-widest">Stage 01</span>
                      <h2 className="text-2xl font-black tracking-tight group-hover:text-red-600 transition-colors">INCURSION</h2>
                    </div>
                    <p className="text-muted-foreground font-medium">Murals, Aerography, Screen Printing, and Recycling Art</p>
                  </div>
                  <div className={`
                    w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-500 border-2
                    ${expandedStages.has(1) ? 'bg-red-600 border-red-500 rotate-180 shadow-[0_0_15px_rgba(239,68,68,0.2)]' : 'bg-muted border-border group-hover:border-red-500/50'}
                  `}>
                    <ChevronDown className={`w-6 h-6 ${expandedStages.has(1) ? 'text-white' : 'text-muted-foreground group-hover:text-red-500'}`} />
                  </div>
                </div>
                {expandedStages.has(1) && (
                  <div className="px-8 pb-8 pt-0 space-y-8 animate-in fade-in slide-in-from-top-4 duration-500">
                    <div className="prose dark:prose-invert max-w-none space-y-4 text-foreground border-t border-border/50 pt-8">
                      <p>
                        <strong>First Steps:</strong> After studying Graphic Design in Margarita and early design work, I leaned fully into visual experimentation. In Austin, I absorbed art and street culture, and the rhythm of making; in Chile, I worked in a Mapuche family&apos;s serigraphy and bookbinding workshop; in Argentina, I hiked and traveled, collecting impressions and cultural resources.
                      </p>
                      <p>
                        I&apos;m a self-taught artist that worked with screen printing, aerography, and mural painting while starting to collect discarded electronics and beach-found wood. Those &ldquo;rescued&rdquo; objects became my first assemblages—confronting the lifespan of our devices and the beauty hidden in waste.
                      </p>
                      <p>
                        <strong>Shared Gallery:</strong> Back on the island, I shared space with my mother, artist Lissette Villamizar, and other fellow artists at Taller de Arte La Casa Amarilla in Pampatar. Then I had my own workshop with a couple of friends as assistants and that space became the engine for my early series of assemblages, with which I created artistic furniture.
                      </p>
                    </div>
                    <BioImageGrid items={BIO_STAGE_IMAGES[1]} onSelect={openGalleryImage} />
                  </div>
                )}
              </div>

              {/* Stage 2 */}
              <div className="bg-card/50 border-2 border-border/40 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
                <div 
                  onClick={() => toggleStage(2)}
                  className="p-8 cursor-pointer flex items-center justify-between group select-none"
                >
                  <div className="flex-grow">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-[10px] font-mono font-black px-2 py-0.5 bg-orange-500/20 text-orange-600 border border-orange-600/30 rounded uppercase tracking-widest">Stage 02</span>
                      <h2 className="text-2xl font-black tracking-tight group-hover:text-orange-500 transition-colors">ASSEMBLAGES</h2>
                    </div>
                    <p className="text-muted-foreground font-medium">Recycled Wood and Acrylic Paintings</p>
                  </div>
                  <div className={`
                    w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-500 border-2
                    ${expandedStages.has(2) ? 'bg-orange-500 border-orange-400 rotate-180 shadow-[0_0_15px_rgba(249,115,22,0.2)]' : 'bg-muted border-border group-hover:border-orange-500/50'}
                  `}>
                    <ChevronDown className={`w-6 h-6 ${expandedStages.has(2) ? 'text-white' : 'text-muted-foreground group-hover:text-orange-500'}`} />
                  </div>
                </div>
                {expandedStages.has(2) && (
                  <div className="px-8 pb-8 pt-0 space-y-8 animate-in fade-in slide-in-from-top-4 duration-500">
                    <div className="prose dark:prose-invert max-w-none space-y-4 text-foreground border-t border-border/50 pt-8">
                      <p>
                        <strong>Language of materials:</strong> From 2011 onward, I refined a series of assemblages in recycled wood, paired with some acrylic paintings. The work earned regional recognition and became part of my identity as an artist who built images out of recovered matter.
                      </p>
                      <p>
                        <strong>Commissions and presence:</strong> I created custom recycled-wood mirror frames for every room at the boutique hotel Isabel La Católica (2013), where I also held a permanent exhibition.
                      </p>
                      <p>
                        In my workshop, we formed a small production team with a graffiti artist (Lios) and a hip-hop artist (Junity), and used La Casa Amarilla as a hub to show my work. Production was done in the workshop.
                      </p>
                    </div>
                    <BioImageGrid items={BIO_STAGE_IMAGES[2]} onSelect={openGalleryImage} />
                  </div>
                )}
              </div>

              {/* Stage 3 */}
              <div className="bg-card/50 border-2 border-border/40 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
                <div 
                  onClick={() => toggleStage(3)}
                  className="p-8 cursor-pointer flex items-center justify-between group select-none"
                >
                  <div className="flex-grow">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-[10px] font-mono font-black px-2 py-0.5 bg-yellow-500/20 text-yellow-600 border border-yellow-600/30 rounded uppercase tracking-widest">Stage 03</span>
                      <h2 className="text-2xl font-black tracking-tight group-hover:text-yellow-500 transition-colors">GROWTH</h2>
                    </div>
                    <p className="text-muted-foreground font-medium">Gallery ties, Own Workshop, Expositions, Museums</p>
                  </div>
                  <div className={`
                    w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-500 border-2
                    ${expandedStages.has(3) ? 'bg-yellow-500 border-yellow-400 rotate-180 shadow-[0_0_15px_rgba(234,179,8,0.2)]' : 'bg-muted border-border group-hover:border-yellow-500/50'}
                  `}>
                    <ChevronDown className={`w-6 h-6 ${expandedStages.has(3) ? 'text-white' : 'text-muted-foreground group-hover:text-yellow-500'}`} />
                  </div>
                </div>
                {expandedStages.has(3) && (
                  <div className="px-8 pb-8 pt-0 space-y-8 animate-in fade-in slide-in-from-top-4 duration-500">
                    <div className="prose dark:prose-invert max-w-none space-y-4 text-foreground border-t border-border/50 pt-8">
                      <p>
                        Those years were about building a sustainable creative ecosystem—studio workflow, collaborators, steady commissions, and a growing network of galleries, curators, and collectors across the island and mainland Venezuela.
                      </p>
                      <p>
                        <strong>Momentum:</strong> My practice expanded to installations and curated series. The workshop became both a studio and a small production house supporting exhibitions and commissions.
                      </p>
                      <p>
                        <strong>Recognition:</strong> Collective exhibitions included Aislados (2012), Artucs (2013 – Second Prize in Painting; 2014 – Third Prize in Sculpture), Colossus (2013 – BVAC), Desde la Orilla (2013 – Museo Alejandro Otero, National Museum), Museo de Arte Colonial de Mérida (2015), plus the solo show Milímetros at Universidad Corporativa Sigo and an artistic residency at La Aldea in Bogotá. In 2016, I registered copyrights for a body of assemblage works.
                      </p>
                    </div>
                    <BioImageGrid items={BIO_STAGE_IMAGES[3]} onSelect={openGalleryImage} />
                  </div>
                )}
              </div>

              {/* Stage 4 */}
              <div className="bg-card/50 border-2 border-border/40 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
                <div 
                  onClick={() => toggleStage(4)}
                  className="p-8 cursor-pointer flex items-center justify-between group select-none"
                >
                  <div className="flex-grow">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-[10px] font-mono font-black px-2 py-0.5 bg-green-500/20 text-green-600 border border-green-600/30 rounded uppercase tracking-widest">Stage 04</span>
                      <h2 className="text-2xl font-black tracking-tight group-hover:text-green-500 transition-colors">EXILE</h2>
                    </div>
                    <p className="text-muted-foreground font-medium">Exile, Survival, and New Vectors</p>
                  </div>
                  <div className={`
                    w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-500 border-2
                    ${expandedStages.has(4) ? 'bg-green-500 border-green-400 rotate-180 shadow-[0_0_15px_rgba(34,197,94,0.2)]' : 'bg-muted border-border group-hover:border-green-500/50'}
                  `}>
                    <ChevronDown className={`w-6 h-6 ${expandedStages.has(4) ? 'text-white' : 'text-muted-foreground group-hover:text-green-500'}`} />
                  </div>
                </div>
                {expandedStages.has(4) && (
                  <div className="px-8 pb-8 pt-0 space-y-8 animate-in fade-in slide-in-from-top-4 duration-500">
                    <div className="prose dark:prose-invert max-w-none space-y-4 text-foreground border-t border-border/50 pt-8">
                      <p>
                        <strong>Venezuela collapses:</strong> In 2017, my family and I left Margarita Island for Montevideo, Uruguay. I closed the workshop; the team disbanded. Survival took over. I drove a taxi full-time to support us while art went on pause for many years.
                      </p>
                      <p>
                        <strong>Learning in the dark:</strong> I leaned into digital disciplines—Digital Illustration and Animation Production (in The Animation Campus, partner of Vancouver Film School), plus online specializations in Game Design, Art for Videogames, Agile Project Management, and Design Thinking. I also created &ldquo;Exiliado&rdquo; (2019), an animation that narrates the reasons and emotions behind our departure, &ldquo;Remares&rdquo; a house building project from recycled plastic and a video game demo: &ldquo;Seven Elements&rdquo;, a 2D pixel art platformer.
                      </p>
                      <p>
                        <strong>New forms:</strong> With limited materials and space, I shifted toward polygonal acrylic abstractions and digital art based on my previous woodworks, structures and color fields that felt like rebuilding from fragments of the past.
                      </p>
                    </div>
                    <BioImageGrid items={BIO_STAGE_IMAGES[4]} onSelect={openGalleryImage} />
                  </div>
                )}
              </div>

              {/* Stage 5 */}
              <div className="bg-card/50 border-2 border-border/40 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
                <div 
                  onClick={() => toggleStage(5)}
                  className="p-8 cursor-pointer flex items-center justify-between group select-none"
                >
                  <div className="flex-grow">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-[10px] font-mono font-black px-2 py-0.5 bg-blue-500/20 text-blue-600 border border-blue-600/30 rounded uppercase tracking-widest">Stage 05</span>
                      <h2 className="text-2xl font-black tracking-tight group-hover:text-blue-500 transition-colors">RESTART</h2>
                    </div>
                    <p className="text-muted-foreground font-medium">Organic Imaginary, Acrylic Paintings, Stickers, Prints, Murals, and Web App Developer</p>
                  </div>
                  <div className={`
                    w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-500 border-2
                    ${expandedStages.has(5) ? 'bg-blue-500 border-blue-400 rotate-180 shadow-[0_0_15px_rgba(59,130,246,0.2)]' : 'bg-muted border-border group-hover:border-blue-500/50'}
                  `}>
                    <ChevronDown className={`w-6 h-6 ${expandedStages.has(5) ? 'text-white' : 'text-muted-foreground group-hover:text-blue-500'}`} />
                  </div>
                </div>
                {expandedStages.has(5) && (
                  <div className="px-8 pb-8 pt-0 space-y-8 animate-in fade-in slide-in-from-top-4 duration-500">
                    <div className="prose dark:prose-invert max-w-none space-y-4 text-foreground border-t border-border/50 pt-8">
                      <p>
                        <strong>Costa Rica, Pacific coast:</strong> After four years in Uruguay, we moved to Puntarenas (Dominical/Uvita/Ojochal). Here, I re-rooted. I teach art  and in schools like Jungle Academy. I sell my products at local shops and fairs, and meet travelers who carry my work onward.
                      </p>
                      <p>
                        My current series blends organic forms with traces of the geometric language I developed during the exile years and all my previous color patterns used in my assemblage series. —abstraction meeting realism— structures breathing into nature. I paint murals and canvases, and I design prints, stickers, apparel (tees, rash guards, shoes, bags), and have developed web-based projects. I also build and maintain my website and will continue exploring interactive/digital paths that connect with my artworks.
                      </p>
                      <p className="font-semibold text-primary">
                        <strong>Invitation:</strong> This is the beginning of a new adventure for me. If you&apos;re here, you&apos;re already part of it and I thank you!
                      </p>
                    </div>
                    <BioImageGrid items={BIO_STAGE_IMAGES[5]} onSelect={openGalleryImage} />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-12">
              <div className="bg-card border border-border rounded-lg p-8">
                <div className="text-center text-muted-foreground italic italic text-muted-foreground">
                  <p>... en proceso ...</p>
                </div>
              </div>
            </div>
          )}
        </section>
      </main>
      
      <Footer />
    </div>
  )
}
